import {
  WSFilterApiOptionsLoader,
  WSFilterOption
} from "@wingspanhq/fe-component-library";
import { SearchPayerRowsFilterParameter } from "@wingspanhq/wingspan-api-sdk";
import { useMemo, useState } from "react";
import { queriesPayerRows } from "@query";
import { selectorSearchRowName } from "../../../shared/selectors/selectorSearchRowName";

/**
 * API-based payer-assignment filter options, with pagination and search.
 *
 * v3 read: `searchPayerRows` (was v1 `useQueryPayerRows`, whose search service
 * throws `NotMigrated` — so these options were always empty). The row carries
 * its assignments inline as `PayerRowEngagement[]`, which replaces v1's
 * `selectorPayerPayeeEngagements` + `selectorPayerPayeeEngagementName` pair:
 * the filtering they did is the `isDefault` check below, and the name they
 * derived is now the row's own `name`.
 *
 * @example
 * const engagementOptions = usePayerEngagementFilterOptions();
 *
 * const filters: WSFilter[] = [
 *   {
 *     title: "Engagement",
 *     name: "engagementId",
 *     type: "select",
 *     apiOptions: engagementOptions
 *   }
 * ];
 */
export function usePayerEngagementFilterOptions(
  baseFilter?: SearchPayerRowsFilterParameter
): WSFilterApiOptionsLoader & { error: unknown } {
  const [searchText, setSearchText] = useState("");

  const query = queriesPayerRows.searchPayerRows.usePaginatedQuery(
    {
      filter: {
        ...baseFilter,
        ...(searchText ? { searchString: { eq: searchText } } : {})
      }
    },
    // Filter options are not worth retrying: a search-row filter the backend
    // rejects answers the same way every time, and each attempt keeps the panel
    // in its loading state instead of showing the caller the failure.
    { retry: false }
  );

  const rows = query.flatData;

  const options = useMemo(() => {
    const result: WSFilterOption<"checkbox">[] = [];

    (rows ?? []).forEach(payer => {
      const payerName = selectorSearchRowName(payer);

      (payer.engagements ?? [])
        .filter(engagement => !engagement.isDefault && !!engagement.id)
        .forEach(engagement => {
          result.push({
            name: engagement.id as string,
            label: [payerName, engagement.name].filter(Boolean).join(" • "),
            helperText: undefined
          });
        });
    });

    return result;
  }, [rows]);

  return {
    // A failed read is terminal: report no options and no loading, so the select
    // settles instead of spinning, and hand the error to the caller — the filter
    // config is the only place that can say the options are missing.
    error: query.error,
    data: query.isError ? [] : options,
    isLoading: query.isInitialLoading,
    pagination: {
      hasNextPage: query.hasNextPage ?? false,
      isFetching: query.isFetching,
      isFetchingNextPage: query.isFetchingNextPage,
      fetchNextPage: query.fetchNextPage
    },
    search: {
      searchText,
      onSearchChange: setSearchText
    }
  };
}
